import React, { useEffect, useMemo, useState, Component } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Search, Save, X, UserCircle2, ChevronRight, UserPlus, Plus, Calendar } from 'lucide-react';
import { MOCK_USERS, LEAD_SOURCES, User } from '../data/mockData';
import { cn, formatCurrency } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
interface RateTablesProps {
  focusUserId?: string | null;
  onConsumeFocus?: () => void;
}
const sortSources = (arr: string[]) => [...arr].sort((a, b) => a.localeCompare(b));
export function RateTables({
  focusUserId,
  onConsumeFocus
}: RateTablesProps) {
  const [users, setUsers] = useState(MOCK_USERS.filter(u => u.model === 'BPS'));
  const [leadSources, setLeadSources] = useState<string[]>(sortSources(LEAD_SOURCES));
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(users[0]?.id || null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, number>>({});
  // Modal state
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddSource, setShowAddSource] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserTitle, setNewUserTitle] = useState('');
  const [newSourceName, setNewSourceName] = useState('');
  useEffect(() => {
    if (focusUserId && users.some(u => u.id === focusUserId)) {
      setSelectedUserId(focusUserId);
      setIsEditing(false);
      onConsumeFocus?.();
    }
  }, [focusUserId, users, onConsumeFocus]);
  const filteredUsers = useMemo(() => {
    return users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.title.toLowerCase().includes(search.toLowerCase()));
  }, [users, search]);
  const selectedUser = users.find(u => u.id === selectedUserId);
  const handleSelectUser = (user: User) => {
    if (isEditing) {
      if (!window.confirm('You have unsaved changes. Discard?')) return;
    }
    setSelectedUserId(user.id);
    setIsEditing(false);
  };
  const startEditing = () => {
    if (selectedUser && selectedUser.rates) {
      const seeded: Record<string, number> = {};
      leadSources.forEach(s => {
        seeded[s] = selectedUser.rates?.[s] ?? 0;
      });
      setEditForm(seeded);
      setIsEditing(true);
    }
  };
  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };
  const saveRates = () => {
    if (!selectedUserId) return;
    setUsers(users.map(u => u.id === selectedUserId ? {
      ...u,
      rates: editForm
    } : u));
    setIsEditing(false);
  };
  const handleRateChange = (source: string, value: string) => {
    const numValue = parseInt(value, 10);
    setEditForm(prev => ({
      ...prev,
      [source]: isNaN(numValue) ? 0 : numValue
    }));
  };
  const handleAddUser = () => {
    const name = newUserName.trim();
    if (!name) return;
    const title = newUserTitle.trim() || 'Loan Officer';
    const rates: Record<string, number> = {};
    leadSources.forEach(s => {
      rates[s] = 0;
    });
    const newUser: User = {
      id: `user-new-${Date.now()}`,
      name,
      title,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`,
      model: 'BPS',
      rates,
      fundedBySource: {},
      fundedMTD: 0,
      fundedLastMonth: 0
    };
    setUsers(prev => [newUser, ...prev]);
    setSelectedUserId(newUser.id);
    setIsEditing(false);
    setNewUserName('');
    setNewUserTitle('');
    setShowAddUser(false);
  };
  const handleAddSource = () => {
    const name = newSourceName.trim();
    if (!name) return;
    if (leadSources.some(s => s.toLowerCase() === name.toLowerCase())) {
      setNewSourceName('');
      setShowAddSource(false);
      return;
    }
    setLeadSources(prev => sortSources([...prev, name]));
    // Seed the new source at 0 bps for every user so rate tables stay complete
    setUsers(prev => prev.map(u => ({
      ...u,
      rates: {
        ...u.rates,
        [name]: u.rates?.[name] ?? 0
      }
    })));
    setEditForm(prev => ({
      ...prev,
      [name]: prev[name] ?? 0
    }));
    setNewSourceName('');
    setShowAddSource(false);
  };
  return <div className="space-y-6" data-id="element-554">
      <div className="flex items-center justify-between pb-2" data-id="element-555">
        <div data-id="element-556">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-557">
            BPS Rate Tables
          </h1>
          <p className="text-sm text-slate-500" data-id="element-558">
            Manage basis points by lead source
          </p>
        </div>
      </div>

      <div className="flex h-[calc(100vh-12rem)] gap-6" data-id="element-559">
        {/* Left: Master List (Fixed Width) */}
        <Card className="w-80 lg:w-96 flex flex-col overflow-hidden shadow-sm border-slate-200/60 shrink-0 rounded-xl" data-id="element-560">
          <CardHeader className="border-b border-slate-100 pb-4 shrink-0 bg-white" data-id="element-561">
            <div className="flex items-center justify-between" data-id="element-562">
              <div data-id="element-563">
                <CardTitle data-id="element-564">BPS Users</CardTitle>
                <CardDescription data-id="element-565">
                  Select a user to manage rates.
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowAddUser(true)} className="bg-slate-900 text-white hover:bg-slate-800 shrink-0" data-id="element-566">
                <UserPlus className="mr-1.5 h-4 w-4" data-id="element-567" /> Add User
              </Button>
            </div>
            <div className="relative mt-3" data-id="element-568">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" data-id="element-569" />
              <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-slate-50 border-slate-200 focus:bg-white" data-id="element-570" />
            </div>
          </CardHeader>
          <div className="flex-1 overflow-y-auto p-2 bg-slate-50/30" data-id="element-571">
            <div className="space-y-1" data-id="element-572">
              {filteredUsers.map(user => {
              const isSelected = selectedUserId === user.id;
              return <button key={user.id} onClick={() => handleSelectUser(user)} className={cn('w-full flex items-center gap-3 rounded-lg p-3 text-left transition-all duration-200 group', isSelected ? 'bg-white shadow-sm ring-1 ring-slate-200' : 'hover:bg-white hover:shadow-sm')} data-id="element-573">
                    <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full bg-slate-200 border border-slate-100" data-id="element-574" />
                    <div className="flex-1 overflow-hidden" data-id="element-575">
                      <div className={cn('font-semibold truncate text-sm', isSelected ? 'text-slate-900' : 'text-slate-700')} data-id="element-576">
                        {user.name}
                      </div>
                      <div className="text-xs text-slate-500 truncate font-medium" data-id="element-577">
                        {user.title}
                      </div>
                    </div>
                    <ChevronRight className={cn('h-4 w-4 transition-transform', isSelected ? 'text-slate-400 translate-x-0' : 'text-transparent -translate-x-2 group-hover:text-slate-300 group-hover:translate-x-0')} data-id="element-578" />
                  </button>;
            })}
              {filteredUsers.length === 0 && <div className="p-8 text-center text-sm text-slate-500 flex flex-col items-center" data-id="element-579">
                  <Search className="h-6 w-6 mb-2 text-slate-300" data-id="element-580" />
                  No users found.
                </div>}
            </div>
          </div>
        </Card>

        {/* Right: Detail View (Fluid Width) */}
        <Card className="flex-1 flex flex-col overflow-hidden shadow-sm border-slate-200/60 bg-white rounded-xl" data-id="element-581">
          <AnimatePresence mode="wait" data-id="element-582">
            {selectedUser ? <motion.div key={selectedUser.id} initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} transition={{
            duration: 0.15
          }} className="flex flex-col h-full" data-id="element-583">
                <CardHeader className="border-b border-slate-100 shrink-0 flex flex-row items-start justify-between bg-white" data-id="element-584">
                  <div className="flex items-center gap-5" data-id="element-585">
                    <img src={selectedUser.avatar} alt={selectedUser.name} className="h-16 w-16 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-586" />
                    <div data-id="element-587">
                      <CardTitle className="text-2xl" data-id="element-588">
                        {selectedUser.name}
                      </CardTitle>
                      <CardDescription className="text-sm font-medium mt-1" data-id="element-589">
                        {selectedUser.title}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-590">
                    <Button variant="outline" onClick={() => setShowAddSource(true)} data-id="element-591">
                      <Plus className="mr-1.5 h-4 w-4" data-id="element-592" /> Add Lead Source
                    </Button>
                    {!isEditing ? <Button onClick={startEditing} className="bg-slate-900 text-white hover:bg-slate-800" data-id="element-593">
                        Edit Rates
                      </Button> : <div className="flex gap-2" data-id="element-594">
                        <Button variant="outline" onClick={cancelEditing} data-id="element-595">
                          Cancel
                        </Button>
                        <Button onClick={saveRates} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-id="element-596">
                          <Save className="mr-2 h-4 w-4" data-id="element-597" /> Save Changes
                        </Button>
                      </div>}
                  </div>
                </CardHeader>
                <div className="flex-1 overflow-y-auto p-8 bg-slate-50/30" data-id="element-598">
                  <div className="max-w-3xl mx-auto" data-id="element-599">
                    <div className="grid grid-cols-2 gap-y-2 border-b border-slate-200 pb-3 mb-4 text-sm font-bold text-slate-500 uppercase tracking-wider" data-id="element-600">
                      <div data-id="element-601">Lead Source</div>
                      <div className="text-right" data-id="element-602">Basis Points (BPS)</div>
                    </div>
                    <div className="space-y-2" data-id="element-603">
                      {leadSources.map(source => <div key={source} className="flex items-center justify-between group p-3 rounded-lg hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-slate-100" data-id="element-604">
                          <div className="text-sm font-semibold text-slate-700" data-id="element-605">
                            {source}
                          </div>
                          <div className="flex items-center gap-2" data-id="element-606">
                            {isEditing ? <div className="flex items-center" data-id="element-607">
                                <Input type="number" className="w-24 text-right font-semibold bg-white border-blue-200 focus:border-blue-500 focus:ring-blue-500" value={editForm[source] ?? 0} onChange={e => handleRateChange(source, e.target.value)} data-id="element-608" />
                                <span className="ml-3 text-sm font-medium text-slate-400 w-8" data-id="element-609">
                                  bps
                                </span>
                              </div> : <div className="flex items-center" data-id="element-610">
                                <Badge variant="secondary" className="text-sm px-3 py-1 font-bold bg-slate-100 text-slate-700" data-id="element-611">
                                  {selectedUser.rates?.[source] || 0}
                                </Badge>
                                <span className="ml-3 text-sm font-medium text-slate-400 w-8" data-id="element-612">
                                  bps
                                </span>
                              </div>}
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Funded Loans breakdown for this LO */}
                  <div className="max-w-3xl mx-auto mt-10" data-id="element-613">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4" data-id="element-614">
                      <div className="text-sm font-bold text-slate-500 uppercase tracking-wider" data-id="element-615">
                        Funded Loans (This Month)
                      </div>
                      <div className="text-xs font-medium text-slate-400" data-id="element-616">
                        {selectedUser.loans.length} loans
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-id="element-617">
                      <Table data-id="element-618">
                        <TableHeader className="bg-slate-50 border-b border-slate-200" data-id="element-619">
                          <TableRow className="hover:bg-transparent" data-id="element-620">
                            <TableHead className="font-semibold text-slate-600 pl-4" data-id="element-621">
                              Borrower
                            </TableHead>
                            <TableHead className="font-semibold text-slate-600" data-id="element-622">
                              Date
                            </TableHead>
                            <TableHead className="font-semibold text-slate-600" data-id="element-623">
                              Lead Source
                            </TableHead>
                            <TableHead className="text-right font-semibold text-slate-600" data-id="element-624">
                              BPS
                            </TableHead>
                            <TableHead className="text-right font-semibold text-slate-600" data-id="element-625">
                              Loan Amount
                            </TableHead>
                            <TableHead className="text-right font-semibold text-slate-600 pr-4" data-id="element-626">
                              Commission
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody data-id="element-627">
                          {selectedUser.loans.map(loan => <TableRow key={loan.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-628">
                              <TableCell className="font-medium text-slate-900 pl-4" data-id="element-629">
                                {loan.borrowerName}
                              </TableCell>
                              <TableCell className="text-slate-500" data-id="element-630">
                                <div className="flex items-center" data-id="element-631">
                                  <Calendar className="w-3 h-3 mr-1.5 text-slate-400" data-id="element-632" />
                                  {new Date(loan.fundedDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                                </div>
                              </TableCell>
                              <TableCell data-id="element-633">
                                <Badge variant="outline" className="text-slate-600 font-medium" data-id="element-634">
                                  {loan.leadSource}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium text-slate-700" data-id="element-635">
                                {selectedUser.rates?.[loan.leadSource] ?? 0} bps
                              </TableCell>
                              <TableCell className="text-right text-slate-600" data-id="element-636">
                                {formatCurrency(loan.loanAmount)}
                              </TableCell>
                              <TableCell className="text-right font-bold text-emerald-700 pr-4" data-id="element-637">
                                {formatCurrency(loan.commissionAmount)}
                              </TableCell>
                            </TableRow>)}
                          {selectedUser.loans.length === 0 && <TableRow data-id="element-638">
                              <TableCell colSpan={6} className="h-24 text-center text-slate-500" data-id="element-639">
                                No funded loans yet this month.
                              </TableCell>
                            </TableRow>}
                          {selectedUser.loans.length > 0 && <TableRow className="bg-slate-50/80 hover:bg-slate-50/80 border-t-2 border-slate-200" data-id="element-640">
                              <TableCell colSpan={5} className="pl-4 font-semibold text-slate-700" data-id="element-641">
                                Total Commission
                              </TableCell>
                              <TableCell className="text-right font-bold text-emerald-700 pr-4" data-id="element-642">
                                {formatCurrency(selectedUser.loans.reduce((sum, l) => sum + l.commissionAmount, 0))}
                              </TableCell>
                            </TableRow>}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </motion.div> : <div className="flex-1 flex flex-col items-center justify-center text-slate-500 bg-slate-50/50" data-id="element-643">
                <UserCircle2 className="h-16 w-16 mb-4 text-slate-300" data-id="element-644" />
                <p className="font-medium" data-id="element-645">
                  Select a user to view their rate table
                </p>
              </div>}
          </AnimatePresence>
        </Card>

        {/* Add User Modal */}
        <Modal open={showAddUser} title="Add BPS User" description="Creates a new user with a blank rate table you can fill in." onClose={() => setShowAddUser(false)} onSubmit={handleAddUser} submitLabel="Add User" submitDisabled={!newUserName.trim()} data-id="element-646">
          <div className="space-y-4" data-id="element-647">
            <div data-id="element-648">
              <label htmlFor="new-user-name" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-649">
                Full name
              </label>
              <Input id="new-user-name" autoFocus placeholder="e.g. Jordan Rivera" value={newUserName} onChange={e => setNewUserName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddUser()} data-id="element-650" />
            </div>
            <div data-id="element-651">
              <label htmlFor="new-user-title" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-652">
                Title <span className="text-slate-400" data-id="element-653">(optional)</span>
              </label>
              <Input id="new-user-title" placeholder="Loan Officer" value={newUserTitle} onChange={e => setNewUserTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddUser()} data-id="element-654" />
            </div>
          </div>
        </Modal>

        {/* Add Lead Source Modal */}
        <Modal open={showAddSource} title="Add Lead Source" description="Adds a new lead source row to every user's rate table at 0 bps." onClose={() => setShowAddSource(false)} onSubmit={handleAddSource} submitLabel="Add Source" submitDisabled={!newSourceName.trim()} data-id="element-655">
          <div data-id="element-656">
            <label htmlFor="new-source-name" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-657">
              Lead source name
            </label>
            <Input id="new-source-name" autoFocus placeholder="e.g. Facebook Ads" value={newSourceName} onChange={e => setNewSourceName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddSource()} data-id="element-658" />
          </div>
        </Modal>
      </div>
    </div>;
}
interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
  children: React.ReactNode;
}
function Modal({
  open,
  title,
  description,
  onClose,
  onSubmit,
  submitLabel,
  submitDisabled,
  children
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-659">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title} data-id="element-660">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-661" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200" data-id="element-662">
            <div className="flex items-start justify-between p-6 pb-4" data-id="element-663">
              <div data-id="element-664">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-665">
                  {title}
                </h2>
                {description && <p className="text-sm text-slate-500 mt-1" data-id="element-666">{description}</p>}
              </div>
              <button onClick={onClose} aria-label="Close dialog" className="text-slate-400 hover:text-slate-600 transition-colors -mt-1 -mr-1 p-1" data-id="element-667">
                <X className="h-5 w-5" data-id="element-668" />
              </button>
            </div>
            <div className="px-6 pb-2" data-id="element-669">{children}</div>
            <div className="flex justify-end gap-2 p-6 pt-4" data-id="element-670">
              <Button variant="outline" onClick={onClose} data-id="element-671">
                Cancel
              </Button>
              <Button onClick={onSubmit} disabled={submitDisabled} className="bg-slate-900 text-white hover:bg-slate-800" data-id="element-672">
                {submitLabel}
              </Button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}